import { useCallback, useState } from "react";
import { Input } from "../../../Input";
import { Box } from "../../../box";
import { formatValueToMonetary, hideTaxNumber } from "../../../../utils/formatter";
import { TermsModal } from "./TermsModal";
import { apiV1 } from "../../../../lib/axios";
import { useAuth } from "../../../../hooks/useAuth";
import { getPixInfo } from "../../../../services/pix.service";
import { identificarTipoChavePix } from "../../../../utils/identificarTipoChavePix";
import { Button } from "../../../../shared/components/button/button";
import { toast } from "react-toastify";
import { sendTransactionMfa } from "../../../../services/mfa.service";
import { TbCircleCheck, TbKey, TbShare } from "react-icons/tb";
import { FilterItemMfaGeneral } from "../../../E-check/style";
import { NavLink } from "react-router-dom";
import bankList from '../../../../services/bankList.json';
import './Style.css';

export function PixPaymentForm({ selectedSimulation, onConfirm, amount, onBack }) {
  const [pixKey, setPixKey] = useState("");
  const [description, setDescription] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [activeTerm, setActiveTerm] = useState(null);

  const [pixKeyResult, setPixKeyResult] = useState(null);
  const [pixLoader, setPixLoader] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState("");

  const [step, setStep] = useState("INPUT_KEY");

  const juros = selectedSimulation.totalToPay - amount;

  const canConfirm = pixKey && termsAccepted && termsAccepted;
  const { user } = useAuth();

  const getActiveTerm = async () => {
    const term = await apiV1.get('/terms/active/PIX_PARCELADO');
    setActiveTerm(term.data);
  };

  useState(() => {
    getActiveTerm();
  }, []);

  const setMfaStep = () => {
    setStep("MFA");
    sendTransactionMfa();
  };

  const handleConfirm = async () => {
    if (!pixKey) return;
    setPixLoader(true);
    const pixKeyType = identificarTipoChavePix(pixKey);
    const response = await onConfirm(pixKey, pixKeyType, description, mfaCode, pixKeyResult.searchProtocol);
    if (response.status === 200) {
      toast.success('Pagamento realizado com sucesso!');
      setStep("SUCCESS");
      handleTransactionSuccess(response);
    }
    setPixLoader(false);
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTerms(false);
  };

  const isTestUser = () => user.taxNumber === '12345678900';

  const handleTestUser = () => {
    setPixKeyResult({
      ReceiverBankName: 'Nu Pagamentos S.A',
      ReceiverName: 'Conta Teste',
      ReceiverISPB: '18236120',
      ReceiverBank: '260',
      ReceiverBankBranch: '1',
      ReceiverBankAccount: '1231232',
      ReceiverBankAccountDigit: '3',
      ReceiverAccountType: '1',
      PixKeyType: '1',
      PixKeyValue: '12345678900',
      ReceiverTaxNumber: '12345678900',
    });
  };

  const fetchPixInfo = async (pixKey) => {
    const response = await getPixInfo(pixKey, identificarTipoChavePix(pixKey));
    return response.data.data;
  };

  const handleSuccess = () => {
    setStep("REVIEW_INFO");
  };

  const getInfos = useCallback(async (key) => {
    if (pixLoader) return;

    if (isTestUser()) {
      return handleTestUser();
    }

    setPixLoader(true);

    try {
      const pixKeyResult = await fetchPixInfo(key);
      setPixKeyResult(pixKeyResult);
      handleSuccess();
    } catch (err) {
      console.error("Erro ao buscar informações da chave Pix:", err);
      toast.error("Erro ao buscar informações da chave Pix. Verifique se a chave está correta.");
    } finally {
      setPixLoader(false);
    }
  }, [pixLoader, user]);

  const handleClick = useCallback(async () => {
    await sendTransactionMfa();
    setIsDisabled(true);
    setCountdown(40);

    const interval = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown === 1) {
          clearInterval(interval);
          setIsDisabled(false);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  }, []);

  const handleTransactionSuccess = async (response) => {
    const pixResponseData = response.data.data;

    if (!user.isMainAccess) {
      toast.success('Transação Pix aguardando aprovação!');
    } else {
      await getComprovante(
        pixResponseData.documentNumber,
        "PixInstallmentOut",
      );
    }
  };

  async function getComprovante(documentNumber, type) {
    const response = await apiV1.post('/voucher/fetch', {
      type,
      documentNumber,
    });

    const url = response.data.url;
    localStorage.removeItem('comprovante');
    setVoucherUrl(url);
  }

  return (
    <Box title="Pagamento Pix Parcelado">
      {/* RESUMO */}
      {
        step !== "SUCCESS" && (
          <div className="p-3 mb-4 rounded bg-dark text-white">
            <p className="fw-semibold mb-1">
              Você pagará{" "}
              <strong>{selectedSimulation.installments}x</strong> de{" "}
              <strong>
                {formatValueToMonetary(selectedSimulation.installmentValue)}
              </strong>
            </p>

            <p className="mb-1">
              Valor solicitado:{" "}
              <strong>
                {formatValueToMonetary(amount)}
              </strong>
            </p>
            <p className="mb-1">
              Total:{" "}
              <strong>
                {formatValueToMonetary(selectedSimulation.totalToPay)}
              </strong>
            </p>

            <p className="text-warning mb-0">
              Juros: {formatValueToMonetary(juros)}
            </p>
          </div>
        )
      }

      {
        step === "INPUT_KEY" && (
          <>
            <Input
              label="Chave Pix"
              value={pixKey}
              onChange={setPixKey}
              placeholder="Digite sua chave Pix"
            />

            <Input
              label="Descrição (opcional)"
              value={description}
              onChange={setDescription}
            />
            <div className="d-flex justify-content-between col-md-12">
              <Button
                variant="secondary"
                size="sm"
                onClick={onBack}
                loading={pixLoader}
              >
                Voltar
              </Button>

              <Button
                variant="primary"
                size="md"
                onClick={() => getInfos(pixKey)}
                loading={pixLoader}
                disabled={!pixKey || pixKey.trim() === ''}
              >
                {pixLoader ? 'Consultando...' : 'Consultar Chave Pix'}
              </Button>
            </div>
          </>
        )
      }

      {
        step === "REVIEW_INFO" && pixKeyResult && (
          <>
            <div className="p-3 rounded">
              <p className="mb-1">
                <strong>Nome do Recebedor:</strong> {pixKeyResult.ReceiverName}
              </p>
              <p className="mb-1">
                <strong>CPF/CNPJ do Favorecido:</strong> {pixKeyResult.ReceiverTaxNumber}
              </p>
              <p className="mb-1">
                <strong>Banco:</strong> {pixKeyResult.ReceiverBankName}
              </p>
            </div>
            {/* TERMOS */}
            <div className="mt-4 p-3 rounded border">
              <p className="mb-2 fw-semibold">Termos e Condições</p>

              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setShowTerms(true)}
              >
                Ler termos do Pix Parcelado
              </button>

              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  disabled={!termsAccepted}
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  id="terms"
                />
                <label className="form-check-label" htmlFor="terms">
                  Li e aceito os termos e condições
                </label>
              </div>
            </div>

            <Button
              variant="primary"
              size={'md'}
              onClick={setMfaStep}
              disabled={!canConfirm}
            >
              Confirmar Pagamento
            </Button>
          </>
        )
      }

      {
        step === "MFA" && (
          <div className="mfa-container">
            <label className="mfa-label">Código de confirmação</label>

            <div className="mfa-row">
              <div className="mfa-input-wrapper">
                <TbKey size={18} className="mfa-icon" />

                <input
                  type="text"
                  placeholder="Insira o código MFA"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                  className="mfa-input"
                />
              </div>

              <button
                className="mfa-link"
                onClick={handleClick}
                disabled={isDisabled}
              >
                Novo Código
              </button>
            </div>

            {isDisabled && (
              <p className="mfa-timer">
                Você pode solicitar um novo código em 0:{countdown} segundos
              </p>
            )}

            <Button
              variant="primary"
              size="md"
              onClick={handleConfirm}
              disabled={mfaCode.length === 0 || pixLoader}
            >
              {pixLoader ? 'Processando...' : 'Confirmar Pagamento'}
            </Button>
          </div>
        )
      }

      {
        step === "SUCCESS" && (
          <>
            <div className="pix-modal-header">
              {user.isMainAccess ? <TbCircleCheck color="#32D16D" size={75} /> : <img src={pendingIcon} style={{ width: '75px', marginBottom: '20px' }} />}
              <h1>{user.isMainAccess ? 'Transferência Pix realizada com sucesso!' : 'Transferência Pix aguardando aprovação!'}</h1>
            </div>

            <div className="pix-modal-body">
              <div className="d-flex">
                <div className="col-md-4">
                  <label>Nome</label>
                  <p>{pixKeyResult?.ReceiverName}</p>
                </div>
                <div className="col-md-4">
                  <label>Instituição de Destino</label>
                  <p>
                    {pixKeyResult?.ReceiverBank} -{' '}
                    {bankList.find(item => item.code == pixKeyResult?.ReceiverBank)?.name}
                  </p>
                </div>
                <div className="col-md-4">
                  <label>CPF/CNPJ do Favorecido</label>
                  <p>{hideTaxNumber(pixKeyResult?.ReceiverTaxNumber)}</p>
                </div>
              </div>
            </div>
            {
              user.isMainAccess &&
              <div className="pix-modal-footer">
                <NavLink
                  to={voucherUrl}
                  target="_blank"
                  className="share-button"
                >
                  Compartilhar comprovante
                  <TbShare size={16} />
                </NavLink>
              </div>
            }
          </>
        )
      }

      {showTerms && (
        <TermsModal
          onClose={() => setShowTerms(false)}
          onAccept={handleAcceptTerms}
          termsContent={activeTerm?.content}
          termsVersion={activeTerm?.version}
          isOpen={showTerms}
        />
      )}
    </Box>
  );
}
